<template>
  <BaseDialog centered contentClass="emitter-form" ref="dialog" persistent :show-actions="true">
    <template slot="title">{{
      isEdit
        ? $t('page.business-intelligence.emitter.edit-dialog.title')
        : $t('page.business-intelligence.emitter.add-dialog.title')
    }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <FormInput
          ref="emitterName"
          :label="$t('page.business-intelligence.emitter.add-dialog.name')"
          is-required
          has-gray-background
          name="name"
          type="name"
          v-model="emitterName"
          :readonly="initialLoading"
          v-validate="'required'"
          @input="onNameInput"
          :error="emitterNameError"
        />
        <div v-if="emitterName.trim().length > 0">
          <FormLabel is-required>{{ $t('page.business-intelligence.emitter.add-dialog.normalized-name') }}</FormLabel>
          <BaseMultiSelectSearch
            ref="aliasesRef"
            :suggestions="suggestions"
            :initial-loading="initialLoading"
            :loading="initialLoading || suggestionsLoading"
            :existing-selections="aliases"
            allow-add-custom
            :empty-selections-label="$t('page.business-intelligence.emitter.aliases.empty-selection')"
            :empty-suggestions-label="$t('page.business-intelligence.emitter.aliases.empty')"
            :description-label="$t('page.business-intelligence.emitter.aliases.description')"
            @updated-selected-names="updateAliases"
            @on-search="refreshSuggestions"
            @all-names-selected="restoreDefaultSuggestions"
          />
        </div>
        <div class="emitter-form__actions">
          <FormButton is-outlined class="emitter-form__cancel" :disabled="submitting" @click.native="close">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="isFormInvalid || submitting">
            <span>{{ $t('actions.save') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DebounceMixin from '@/mixins/debounce-mixin';
import BaseDialog from '@/components/base/BaseDialog';
import BaseMultiSelectSearch from '@/components/base/BaseMultiSelectSearch.vue';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';

// Adds an emitter to the shared catalogue, or edits one, with the aliases it
// appears under in tenders. Like the competitor dialogs, the aliases are suggested
// from the names found in the tender data (aloha.bi /emitter/suggestion); any other
// name can still be added by hand.
const SUGGESTION_DEBOUNCE_MS = 1000;

export default {
  name: 'EmitterFormDialog',
  mixins: [DebounceMixin],
  components: {
    BaseDialog,
    BaseMultiSelectSearch,
    FormButton,
    FormInput,
    FormLabel,
  },
  data() {
    return {
      existingEmitter: null,
      emitterName: '',
      aliases: [],
      // suggestions for the emitter name, restored once a search in the alias box is cleared
      defaultSuggestions: [],
      suggestions: [],
      suggestionsLoading: false,
      suggestionRequestId: 0,
      // id of the outstanding alias-box search, if that is what is pending (not a default load)
      aliasSearchRequestId: null,
      initialLoading: false,
      submitting: false,
      // bumped on every open(): a save that ends after the dialog was reopened must not close it
      session: 0,
    };
  },
  computed: {
    ...mapGetters('biDashboard', ['emitters']),
    isEdit() {
      return this.existingEmitter !== null;
    },
    isFormInvalid() {
      return (
        this.emitterName.trim().length === 0 ||
        this.aliases.length === 0 ||
        this.initialLoading ||
        this.emitterNameError !== undefined
      );
    },
    // Only the loaded page is known here; the API refuses the rest with DUPLICATED_EMITTER.
    emitterNameError() {
      const name = this.emitterName.trim().toLowerCase();
      if (name.length === 0) return undefined;
      const duplicate = (this.emitters || []).some(
        (emitter) =>
          emitter.name.trim().toLowerCase() === name &&
          (!this.existingEmitter || emitter.id !== this.existingEmitter.id),
      );
      return duplicate ? this.$t('error.code.DUPLICATED_EMITTER') : undefined;
    },
  },
  methods: {
    ...mapActions('biDashboard', [
      'addEmitter',
      'editEmitter',
      'getEmitter',
      'getEmitterNameSuggestion',
      'cancelGetEmitterNameSuggestion',
    ]),
    // `emitter` is undefined to add one; an EmitterResponse, or a list row ({ id, name })
    // whose aliases are then fetched, to edit one.
    open(emitter) {
      this.session += 1;
      this.existingEmitter = emitter || null;
      this.emitterName = emitter ? emitter.name || '' : '';
      this.aliases = [];
      this.defaultSuggestions = [];
      this.suggestions = [];
      this.suggestionsLoading = false;
      this.aliasSearchRequestId = null;
      this.initialLoading = false;
      this.submitting = false;

      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.emitterName && this.$refs.emitterName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });

      if (!emitter) return;

      if (emitter.emitter_names) {
        this.aliases = this.aliasesOf(emitter);
        this.loadSuggestions(this.emitterName);
        return;
      }
      // The load belongs to this session: once the dialog is reopened for another
      // emitter, nothing from it may touch the form (a cancelled fetch resolves empty).
      const session = this.session;
      this.initialLoading = true;
      this.getEmitter(emitter.id)
        .then((loaded) => {
          if (session !== this.session || !loaded) return;
          this.existingEmitter = loaded;
          this.emitterName = loaded.name;
          this.aliases = this.aliasesOf(loaded);
          this.loadSuggestions(this.emitterName);
        })
        .catch((error) => {
          if (session === this.session) this.$toast.error(error);
        })
        .finally(() => {
          if (session === this.session) this.initialLoading = false;
        });
    },
    onNameInput(name) {
      this.debounce(() => {
        if (this.emitterNameError) {
          this.cancelGetEmitterNameSuggestion();
        } else {
          this.loadSuggestions(name);
        }
      }, SUGGESTION_DEBOUNCE_MS);
    },
    // Fetches the names resembling `seed`. Those for the emitter name are the default
    // list; a search typed in the alias box only replaces what is shown.
    loadSuggestions(seed, { asDefault = true } = {}) {
      const name = (seed || '').trim();
      if (name.length === 0) {
        if (asDefault) this.defaultSuggestions = [];
        this.suggestions = this.defaultSuggestions;
        return Promise.resolve();
      }

      const requestId = ++this.suggestionRequestId;
      this.aliasSearchRequestId = asDefault ? null : requestId;
      this.suggestionsLoading = true;
      return this.getEmitterNameSuggestion(name)
        .then((response) => {
          // undefined when superseded by a newer request (cancelled)
          if (!response || requestId !== this.suggestionRequestId) return;
          if (asDefault) this.defaultSuggestions = response;
          this.suggestions = response;
        })
        .catch(() => {
          // suggestions are a convenience: the aliases can still be typed
        })
        .finally(() => {
          if (requestId === this.suggestionRequestId) this.suggestionsLoading = false;
        });
    },
    refreshSuggestions(query) {
      if (query && query.length > 0) {
        this.loadSuggestions(query, { asDefault: false });
      } else {
        this.restoreDefaultSuggestions();
      }
    },
    restoreDefaultSuggestions() {
      // An alias-box search still in flight must not replace the defaults once it lands;
      // a pending load of the defaults themselves is left to complete.
      if (this.aliasSearchRequestId !== null && this.aliasSearchRequestId === this.suggestionRequestId) {
        this.suggestionRequestId += 1;
        this.aliasSearchRequestId = null;
        this.suggestionsLoading = false;
        this.cancelGetEmitterNameSuggestion();
      }
      this.suggestions = this.defaultSuggestions;
    },
    aliasesOf(emitter) {
      return (emitter.emitter_names || []).map((alias) => alias.normalized_name);
    },
    close() {
      this.session += 1;
      this.suggestionRequestId += 1;
      this.debounce(() => {}, 0, true);
      this.cancelGetEmitterNameSuggestion();
      if (this.$refs.aliasesRef) {
        this.$refs.aliasesRef.reset();
      }
      this.$refs.dialog.isModalOpen = false;
    },
    updateAliases(names) {
      this.aliases = [...names];
    },
    async onSubmit() {
      if (this.isFormInvalid || this.submitting) return;
      this.submitting = true;

      const session = this.session;
      const payload = { name: this.emitterName.trim(), normalizedNames: this.aliases };
      const request = this.isEdit
        ? this.editEmitter({ id: this.existingEmitter.id, ...payload })
        : this.addEmitter(payload);

      try {
        const emitter = await request;
        // the dialog was closed and reopened meanwhile: leave the new session alone
        if (session !== this.session) return;
        this.$toast.success(
          this.$t(
            this.isEdit
              ? 'page.business-intelligence.emitter.edit-dialog.edit-success'
              : 'page.business-intelligence.emitter.add-dialog.add-success',
          ),
        );
        this.$emit('action-succeeded', emitter);
        this.close();
      } catch (error) {
        // stays open so a duplicate name can be corrected
        this.$toast.error(error);
      } finally {
        if (session === this.session) this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__emitter-form {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__content {
    padding: 8px 32px 32px 32px !important;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}
.emitter-form {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }
}
</style>
